import { useEffect, useState } from "react";
import { apiRequest } from "../services/apiClient";

const EMPTY = { host: "", port: 587, secure: false, user: "", password: "", fromEmail: "info@circuitprinting.com", orderFromEmail: "orders@circuitprinting.com", fromName: "Circuit Printing Solutions" };

export default function AdminEmailSettings() {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [recipient, setRecipient] = useState("");
  const [testType, setTestType] = useState("connection");
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    async function load() {
      try {
        const data = await apiRequest("/admin/settings/smtpSettings");
        setForm(prev => ({ ...prev, ...data }));
      } catch (error) {
        setMessage(error.message);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  function update(key, value) {
    setForm(prev => ({ ...prev, [key]: value }));
  }

  async function save(e) {
    e.preventDefault();
    setSaving(true);
    setMessage("");
    try {
      const data = await apiRequest("/admin/settings/smtpSettings", { method: "PUT", body: JSON.stringify(form) });
      setForm(data);
      setMessage("SMTP settings saved.");
    } catch (error) {
      setMessage(error.message);
    } finally {
      setSaving(false);
    }
  }

  async function testEmail() {
    setTesting(true);
    setMessage("");
    try {
      const data = await apiRequest("/admin/smtp/test", {
        method: "POST",
        body: JSON.stringify({ recipient, type: testType }),
      });
      setMessage(data.message || "Test email sent.");
    } catch (error) {
      setMessage(error.message);
    } finally {
      setTesting(false);
    }
  }

  if (loading) return <div style={{ padding: 60, color: "rgba(255,255,255,0.5)" }}>Loading email settings...</div>;

  return (
    <div style={{ width: "100%", maxWidth: "none" }}>
      <style>{".smtp-settings-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); } .smtp-settings-wide { grid-column: 1 / -1; } @media (max-width: 760px) { .smtp-settings-grid { grid-template-columns: 1fr; } .smtp-settings-wide { grid-column: auto; } }"}</style>
      <div style={{ marginBottom: 24 }}>
        <h2 style={{ color: "#fff", margin: 0 }}>Email &amp; SMTP Settings</h2>
        <p style={{ color: "rgba(255,255,255,0.45)", fontSize: 13 }}>
          These settings send approval and rejection emails from your company. Supplier details are never included.
        </p>
      </div>
      <form className="adm-card" onSubmit={save}>
        <div className="adm-card-header"><span className="adm-card-title">SMTP connection</span></div>
        <div className="smtp-settings-grid" style={{ padding: 22, display: "grid", gap: 14 }}>
          <div className="smtp-settings-wide"><Field label="SMTP host" value={form.host} onChange={v => update("host", v)} placeholder="smtp.yourprovider.com" /></div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
            <Field label="Port" type="number" value={form.port} onChange={v => update("port", v)} placeholder="587" />
            <label style={labelStyle}><span>Secure connection</span><input type="checkbox" checked={Boolean(form.secure)} onChange={e => update("secure", e.target.checked)} /></label>
          </div>
          <Field label="SMTP username" value={form.user} onChange={v => update("user", v)} placeholder="your mailbox username" />
          <Field label="SMTP password" type="password" value={form.password} onChange={v => update("password", v)} placeholder="Leave ******** unchanged to keep it" />
          <div className="smtp-settings-wide" style={{ height: 1, background: "rgba(255,255,255,0.08)", margin: "6px 0" }} />
          <Field label="From email" type="email" value={form.fromEmail} onChange={v => update("fromEmail", v)} placeholder="info@circuitprinting.com" />
          <Field label="Orders from email" type="email" value={form.orderFromEmail} onChange={v => update("orderFromEmail", v)} placeholder="orders@circuitprinting.com" />
          <Field label="From name" value={form.fromName} onChange={v => update("fromName", v)} placeholder="Circuit Printing Solutions" />
          <div className="smtp-settings-wide" style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <button className="adm-btn adm-btn-primary" disabled={saving}>{saving ? "Saving..." : "Save email settings"}</button>
            {message && <span style={{ color: message === "SMTP settings saved." ? "#4ade80" : "#f87171", fontSize: 13 }}>{message}</span>}
          </div>
        </div>
      </form>
      <section className="adm-card" style={{ marginTop: 18 }}>
        <div className="adm-card-header"><span className="adm-card-title">Test email delivery</span></div>
        <div className="smtp-settings-grid" style={{ padding: 22, display: "grid", gap: 14 }}>
          <p className="smtp-settings-wide" style={{ margin: 0, color: "var(--adm-muted)", fontSize: 13 }}>Save SMTP settings first. These tests send only to the address below and do not create orders, customers, or contact messages.</p>
          <Field label="Test recipient email" type="email" value={recipient} onChange={setRecipient} placeholder="you@example.com" />
          <label style={labelStyle}><span>Workflow to test</span><select value={testType} onChange={e => setTestType(e.target.value)} style={inputStyle}>
            <option value="connection">SMTP connection</option>
            <option value="invoice">Order / invoice email</option>
            <option value="customer">New customer email</option>
            <option value="contact">Contact Us notification</option>
          </select></label>
          <button type="button" className="adm-btn adm-btn-primary smtp-settings-wide" disabled={testing || !recipient} onClick={testEmail}>
            {testing ? "Testing..." : "Send test email"}
          </button>
        </div>
      </section>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", placeholder }) {
  return <label style={labelStyle}><span>{label}</span><input type={type} value={value ?? ""} onChange={e => onChange(type === "number" ? Number(e.target.value) : e.target.value)} placeholder={placeholder} required={label !== "From name"} style={inputStyle} /></label>;
}

const labelStyle = { display: "flex", flexDirection: "column", gap: 7, color: "rgba(255,255,255,0.65)", fontSize: 12, fontWeight: 600 };
const inputStyle = { background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.14)", borderRadius: 6, padding: "10px 12px", color: "#fff", fontFamily: "inherit", fontSize: 14 };
